import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Link as LinkIcon,
  Code as CodeIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { deploymentInstanceService } from '../services/DeploymentInstanceService';
import { guardrailsService } from '../services/GuardrailsService';

// Types
interface FilterConfig {
  enabled: boolean;
  mediaType: string;
}

interface GuardrailsConfig {
  inputFilters: {
    toxicLanguage: FilterConfig;
    detectJailbreak: FilterConfig;
  };
  outputFilters: {
    toxicLanguage: FilterConfig;
    detectPII: FilterConfig;
    regexMatch: FilterConfig;
  };
  connections: string[];
}

export interface GuardrailValidator {
  name: string;
  enabled: boolean;
  on_fail: string;
  regex?: string;
}

export interface GuardrailApplyPayload {
  id?: string;
  guardrail_name: string;
  validators: {
    input: GuardrailValidator[];
    output: GuardrailValidator[];
  };
  deployment_ids: string[];
}

export interface DeployedInstance {
  instance_id: string;
  instance_name: string;
  instance_status: string;
  model_name: string;
  guardrail_id: string;
}

// Filter card data
const inputFilterTypes = [
  {
    id: 'toxicLanguage',
    label: 'Toxic Language',
    icon: <WarningIcon />,
    color: '#ef4444',
    bgcolor: '#fee2e2',
    description: 'Violent or graphic content'
  },
  {
    id: 'detectJailbreak',
    label: 'Detect Jailbreak',
    icon: <BlockIcon />,
    color: '#f59e0b',
    bgcolor: '#fef3c7',
    description: 'Harmful or discriminatory content'
  }
];

const outputFilterTypes = [
  {
    id: 'toxicLanguage',
    label: 'Toxic Language',
    icon: <WarningIcon />,
    color: '#ef4444',
    bgcolor: '#fee2e2',
    description: 'Violent or graphic content'
  },
  {
    id: 'detectPII',
    label: 'Detect PII',
    icon: <PsychologyIcon />,
    color: '#10b981',
    bgcolor: '#d1fae5',
    description: 'Personally identifiable information'
  },
  {
    id: 'regexMatch',
    label: 'Regex Match',
    icon: <CodeIcon />,
    color: '#3b82f6',
    bgcolor: '#dbeafe',
    description: 'Custom pattern matching'
  }
];

const NAME_MAP: Record<string, string> = {
  toxicLanguage: "toxic_language",
  detectJailbreak: "detect_jailbreak",
  detectPII: "detect_pii",
  regexMatch: "regex_match"
};

const ON_FAIL_DEFAULTS: Record<string, string> = {
  toxic_language: "EXCEPTION",
  detect_jailbreak: "EXCEPTION",
  detect_pii: "FIX",
  regex_match: "EXCEPTION"
};

// Validation Schema
const validationSchema = Yup.object({
  guardrailName: Yup.string()
    .required('Guardrail name is required')
    .trim()
    .min(1, 'Guardrail name cannot be empty'),
  connections: Yup.array()
    .min(1, 'At least one deployment must be selected')
    .required('Deployment selection is required'),
  regexPattern: Yup.string().when('outputFilters.regexMatch.enabled', {
    is: true,
    then: (schema) => schema.required('Regex pattern is required when Regex Match is enabled'),
    otherwise: (schema) => schema
  })
});

export function GuardrailsPage() {
  const navigate = useNavigate();
  const { id: routeGuardrailId } = useParams();
  const isEditMode = Boolean(routeGuardrailId);
  
  const [deployedInstances, setDeployedInstances] = useState<DeployedInstance[]>([]);
  const [existingDeploymentIds, setExistingDeploymentIds] = useState<string[]>([]);
  const existingValidatorsRef = useRef<any>(null);

  const formik = useFormik({
    initialValues: {
      guardrailName: '',
      regexPattern: '',
      inputFilters: {
        toxicLanguage: { enabled: true, mediaType: 'text' },
        detectJailbreak: { enabled: true, mediaType: 'text' },
      },
      outputFilters: {
        toxicLanguage: { enabled: true, mediaType: 'text' },
        detectPII: { enabled: true, mediaType: 'text' },
        regexMatch: { enabled: false, mediaType: 'text' },
      },
      connections: [] as string[]
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = serializeGuardrailsPayload(values);

        if (isEditMode && routeGuardrailId) {
          payload.id = routeGuardrailId;

          const sameValidators =
            JSON.stringify(payload.validators) ===
            JSON.stringify(existingValidatorsRef.current);

          const sameDeployments =
            JSON.stringify([...payload.deployment_ids].sort()) ===
            JSON.stringify([...existingDeploymentIds].sort());

          if (sameValidators && sameDeployments) {
            toast.info("No changes detected.");
            setSubmitting(false);
            return;
          }
        }

        await guardrailsService.applyGuardrails(payload);
        toast.success("Guardrails applied successfully");
        navigate('/guardrails');
      } catch (error) {
        toast.error("Failed to apply guardrails");
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const deployedInstances = await deploymentInstanceService.getDeploymentInstances();
        setDeployedInstances(deployedInstances);
      } catch (error) {
        console.error('Error fetching deployment instances:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!isEditMode || !routeGuardrailId) return;

    const fetchGuardrailForEdit = async () => {
      try {
        const existing = await guardrailsService.getGuardrailById(routeGuardrailId);
        existingValidatorsRef.current = existing.validators;
        setExistingDeploymentIds(existing.deployment_ids || []);

        const regexValidator = existing.validators.output.find(
          (v: any) => v.name === "regex_match"
        );

        const newInputFilters = { ...formik.values.inputFilters };
        const newOutputFilters = { ...formik.values.outputFilters };

        existing.validators.input.forEach((v: any) => {
          const uiKey = Object.keys(NAME_MAP).find(k => NAME_MAP[k] === v.name);
          if (uiKey && newInputFilters[uiKey as keyof typeof newInputFilters]) {
            newInputFilters[uiKey as keyof typeof newInputFilters].enabled = v.enabled;
          }
        });

        existing.validators.output.forEach((v: any) => {
          const uiKey = Object.keys(NAME_MAP).find(k => NAME_MAP[k] === v.name);
          if (uiKey && newOutputFilters[uiKey as keyof typeof newOutputFilters]) {
            newOutputFilters[uiKey as keyof typeof newOutputFilters].enabled = v.enabled;
          }
        });

        formik.setValues({
          guardrailName: existing.guardrail_name,
          regexPattern: regexValidator?.regex || "",
          inputFilters: newInputFilters,
          outputFilters: newOutputFilters,
          connections: existing.deployment_ids || []
        });
      } catch (err) {
        console.error("Failed to load guardrail for edit", err);
        toast.error("Failed to load guardrail");
      }
    };

    fetchGuardrailForEdit();
  }, [isEditMode, routeGuardrailId]);

  // In edit mode, show all instances. In create mode, show only those without guardrails
  const runningInstances = isEditMode 
    ? deployedInstances 
    : deployedInstances.filter(d => d.guardrail_id === null);

  const toggleInputFilter = useCallback((filterId: string) => {
    formik.setFieldValue(`inputFilters.${filterId}.enabled`, 
      !formik.values.inputFilters[filterId as keyof typeof formik.values.inputFilters]?.enabled
    );
  }, [formik]);

  const toggleOutputFilter = useCallback((filterId: string) => {
    formik.setFieldValue(`outputFilters.${filterId}.enabled`, 
      !formik.values.outputFilters[filterId as keyof typeof formik.values.outputFilters]?.enabled
    );
  }, [formik]);

  const serializeGuardrailsPayload = (values: typeof formik.values): GuardrailApplyPayload => {
    const buildValidator = (key: string, value: any) => {
      const backendName = NAME_MAP[key];
      return {
        name: backendName,
        enabled: value.enabled,
        ...(backendName === "regex_match" ? { regex: values.regexPattern } : {}),
        on_fail: ON_FAIL_DEFAULTS[backendName] || "EXCEPTION"
      };
    };

    const inputValidators = Object.entries(values.inputFilters).map(
      ([key, value]: any) => buildValidator(key, value)
    );

    const outputValidators = Object.entries(values.outputFilters).map(
      ([key, value]: any) => buildValidator(key, value)
    );

    return {
      guardrail_name: values.guardrailName,
      validators: {
        input: inputValidators,
        output: outputValidators
      },
      deployment_ids: values.connections
    };
  };

  const inputFiltersEnabled = Object.values(formik.values.inputFilters).filter(f => f.enabled).length;
  const outputFiltersEnabled = Object.values(formik.values.outputFilters).filter(f => f.enabled).length;

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <ShieldIcon sx={{ fontSize: 40, color: '#2563eb' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Guardrails Configuration
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure content safety filters for AI model deployments
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={4}>
            {/* Section 0: Guardrail Name */}
            <Paper 
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                bgcolor: '#fff'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                  Guardrail Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Give a unique name for this guardrail configuration
                </Typography>
              </Box>

              <input
                type="text"
                name="guardrailName"
                value={formik.values.guardrailName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter guardrail name"
                disabled={isEditMode || formik.isSubmitting}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${formik.touched.guardrailName && formik.errors.guardrailName ? '#ef4444' : '#cbd5e1'}`,
                  fontSize: '1rem',
                  backgroundColor: '#fff'
                }}
              />
              {formik.touched.guardrailName && formik.errors.guardrailName && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {formik.errors.guardrailName}
                </FormHelperText>
              )}
            </Paper>

            {/* Section 1: Input Filters */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 3, 
                border: '1px solid #e2e8f0',
                bgcolor: '#fff'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                  Input Filters
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Control what content can be sent to AI models
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {inputFilterTypes.map((filter) => {
                  const isEnabled = formik.values.inputFilters[filter.id as keyof typeof formik.values.inputFilters]?.enabled ?? false;

                  return (
                    <Grid key={filter.id}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          border: isEnabled ? `2px solid ${filter.color}` : '2px solid #e2e8f0',
                          borderRadius: 2,
                          bgcolor: isEnabled ? `${filter.bgcolor}40` : '#fafbfc',
                          transition: 'all 0.2s',
                          height: 220,
                          '&:hover': {
                            boxShadow: isEnabled ? `0 4px 12px ${filter.color}40` : '0 2px 8px rgba(0,0,0,0.08)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: filter.bgcolor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: filter.color
                              }}
                            >
                              {filter.icon}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                              {filter.label}
                            </Typography>
                          </Box>

                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            {filter.description}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                              {isEnabled ? 'Enabled' : 'Disabled'}
                            </Typography>
                            <Switch
                              checked={isEnabled}
                              onChange={() => toggleInputFilter(filter.id)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: filter.color,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: filter.color,
                                },
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Section 2: Output Filters */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 3, 
                border: '1px solid #e2e8f0',
                bgcolor: '#fff'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                  Output Filters
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Control what content AI models can generate
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {outputFilterTypes.map((filter) => {
                  const isEnabled = formik.values.outputFilters[filter.id as keyof typeof formik.values.outputFilters]?.enabled ?? false;
                  const isRegexMatch = filter.id === 'regexMatch';
                  
                  return (
                    <Grid key={filter.id}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          border: isEnabled ? `2px solid ${filter.color}` : '2px solid #e2e8f0',
                          borderRadius: 2,
                          bgcolor: isEnabled ? `${filter.bgcolor}40` : '#fafbfc',
                          transition: 'all 0.2s',
                          height: 220,
                          minWidth: 235,
                          '&:hover': {
                            boxShadow: isEnabled ? `0 4px 12px ${filter.color}40` : '0 2px 8px rgba(0,0,0,0.08)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: isRegexMatch ? 0 : 2 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: filter.bgcolor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: filter.color
                              }}
                            >
                              {filter.icon}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                              {filter.label}
                            </Typography>
                          </Box>

                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column'}}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                              {filter.description}
                            </Typography>

                            {isRegexMatch && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 2, display: 'block' }}>
                                  Regex Pattern
                                </Typography>
                                <input
                                  type="text"
                                  name="regexPattern"
                                  value={formik.values.regexPattern}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder="e.g., ^[A-Z0-9]+$"
                                  style={{
                                    width: '100%',
                                    padding: '5px 7px',
                                    borderRadius: '4px',
                                    border: `1px solid ${formik.touched.regexPattern && formik.errors.regexPattern ? '#ef4444' : '#e2e8f0'}`,
                                    fontSize: '0.813rem',
                                    fontFamily: 'monospace',
                                    backgroundColor: isEnabled ? '#fff' : '#f8f9fa',
                                  }}
                                />
                                {formik.touched.regexPattern && formik.errors.regexPattern && (
                                  <FormHelperText error sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                                    {formik.errors.regexPattern}
                                  </FormHelperText>
                                )}
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mr: 1 }}>
                              {isEnabled ? 'Enabled' : 'Disabled'}
                            </Typography>
                            <Switch
                              checked={isEnabled}
                              onChange={() => toggleOutputFilter(filter.id)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: filter.color,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: filter.color,
                                },
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Section 3: Connections */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 3, 
                border: '1px solid #e2e8f0',
                bgcolor: '#fff'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                  Deployment Instances <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select which deployments to apply these guardrails to
                </Typography>
              </Box>

              <FormControl fullWidth error={formik.touched.connections && Boolean(formik.errors.connections)}>
                <InputLabel id="connections-label">Select Deployments</InputLabel>
                <Select
                  labelId="connections-label"
                  multiple
                  name="connections"
                  value={formik.values.connections}
                  onChange={(e) => formik.setFieldValue('connections', e.target.value)}
                  onBlur={formik.handleBlur}
                  input={<OutlinedInput label="Select Deployments" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const instance = runningInstances.find(d => d.instance_id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={(instance?.model_name || value)?.replace(/^\/models\//, '')}
                            size="small"
                            sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 600 }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  {runningInstances.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No running deployments available
                      </Typography>
                    </MenuItem>
                  ) : (
                    runningInstances.map((instance) => (
                      <MenuItem key={instance.instance_id} value={instance.instance_id}>
                        <Checkbox checked={formik.values.connections.indexOf(instance.instance_id) > -1} />
                        <Box sx={{ flex: 1, ml: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <ListItemText
                              primary={instance.model_name}
                              secondary={`${instance.instance_name} • ${instance.instance_id}`}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: '0.938rem'
                              }}
                              secondaryTypographyProps={{
                                fontSize: '0.813rem'
                              }}
                            />
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              label="running"
                              size="small"
                              sx={{
                                bgcolor: '#dcfce7',
                                color: '#16a34a',
                                fontWeight: 600,
                                height: 22,
                                '& .MuiChip-icon': {
                                  color: '#16a34a'
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formik.touched.connections && formik.errors.connections && (
                  <FormHelperText error>
                    {formik.errors.connections}
                  </FormHelperText>
                )}
              </FormControl>

              {formik.values.connections.length > 0 && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: '#eff6ff', 
                    borderRadius: 2,
                    border: '1px solid #bfdbfe'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>Selected:</strong> Guardrails will be applied to {formik.values.connections.length} deployment{formik.values.connections.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}

              {runningInstances.length === 0 && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: '#fef3c7', 
                    borderRadius: 2,
                    border: '1px solid #fde68a'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>Note:</strong> No running deployments found. Please deploy a model first to apply guardrails.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBackIcon />}
                 onClick={() => navigate('/guardrails')}
                sx={{
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                    transform: 'translateY(-2px)', 
                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                disabled={formik.isSubmitting || formik.values.connections.length === 0}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)'
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {formik.isSubmitting ? 'Saving Configuration...' : 'Save Configuration'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Container>
    </Box>
  );
}

export default GuardrailsPage;